import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';

import buyerModule from '../../../api/modules/buyer.module';
import useDebounce from '../../../hooks/useDebounce';
import { track } from '../../../analytics/track';
import { EVENTS } from '../../../analytics/events';
import { showToast } from '../../../utils/toast';
import { SavedTableView } from './SavedTable.view';

/**
 * The buyer's collection, the container.
 *
 * Everything about the data is unchanged: the same `['saved-cards', {page,
 * limit: 10, search}]` key, the same `buildParams`, `getSavedCards`,
 * `keepPreviousData`, the same `unsaveCard(row.product)` call and the same
 * `invalidateQueries(['saved-cards'])` afterwards, the same
 * `/marketplace/card/:product` destination, and the same `COLLECTION_VIEWED`
 * event fired exactly once with `item_count`.
 */
export default function SavedTable() {
  const navigate = useNavigate();
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  /* View mode sits here rather than in the view for the same reason the search
     term does: the view is pure and holds no state at all. It is NOT persisted
     — a collection is small enough that the choice is made per visit, and a
     remembered view that disagrees with what you last saw is worse than a
     default. 'list' is the default because the table is what this page has
     always been; the grid is the alternative Junaid asked for. */
  const [view, setView] = useState('list');

  const cardHref = (row) => `/marketplace/card/${row.product}`;

  const queryClient = useQueryClient();
  const debouncedSearch = useDebounce(searchTerm, 500);

  const buildParams = (params) =>
    Object.fromEntries(
      Object.entries(params).filter(
        ([, value]) =>
          value !== '' &&
          value !== null &&
          value !== undefined &&
          !(typeof value === 'number' && isNaN(value)),
      ),
    );

  const queryKey = useMemo(
    () => [
      'saved-cards',
      {
        page: currentPage,
        limit: 10,
        search: debouncedSearch?.trim() || '',
      },
    ],
    [currentPage, debouncedSearch],
  );

  const { data: queryData, isFetching, isError, refetch } = useQuery({
    queryKey,
    queryFn: async () => {
      const params = buildParams(queryKey[1]);
      const { success, data: response, error } = await buyerModule.getSavedCards(params);

      if (!success) {
        console.error('Error fetching saved cards:', error);
        return { favourites: [], page: 1, page_size: 10, total_pages: 0 };
      }

      return response || { favourites: [], page: 1, page_size: 10, total_pages: 0 };
    },
    keepPreviousData: true,
  });

  const data = queryData?.favourites || [];
  const collectionViewedFiredRef = useRef(false);

  useEffect(() => {
    if (collectionViewedFiredRef.current) return;
    if (queryData?.total_pages === undefined) return;
    collectionViewedFiredRef.current = true;
    track(EVENTS.COLLECTION_VIEWED, {
      item_count: queryData?.total ?? data.length,
    });
  }, [queryData, data.length]);

  const handleRemove = async (row) => {
    if (!row) return;
    try {
      const res = await buyerModule.unsaveCard(row.product);
      if (res.success) {
        showToast.success('Card removed from favourites');
        queryClient.invalidateQueries(['saved-cards']);
      } else {
        showToast.error(res.error || 'Failed to remove card');
      }
    } catch (err) {
      showToast.error('Something went wrong');
      console.error(err);
    }
  };

  return (
    <SavedTableView
      rows={data}
      loading={isFetching}
      error={isError ? 'We could not reach your collection.' : null}
      onRetry={refetch}
      search={searchTerm}
      onSearchChange={setSearchTerm}
      page={queryData?.page || 1}
      pageCount={queryData?.total_pages || 0}
      pageSize={queryData?.page_size || 10}
      total={queryData?.total ?? data.length}
      onPageChange={setCurrentPage}
      onOpenCard={(row) => navigate(cardHref(row))}
      onRemove={handleRemove}
      view={view}
      onViewChange={setView}
      cardHref={cardHref}
    />
  );
}
